import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { DefaultButton } from "@/components/DefaultButton";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";
import { translateStreakMissionDescription } from "@/lib/streakMissionTranslations";
import { getRewardIcon } from "./missionConstants";
import { shouldOpenUrlFirst } from "@/routes/(game)/missions/daily-mission";
import type { Mission } from "@/lib/types/api";

export function MissionList({
  missions,
  isLoading,
  activeMissionId,
  onClaim,
  onStart,
  clickedMissions,
  onMissionClicked,
}: {
  missions: Mission[];
  isLoading: boolean;
  activeMissionId: string | null;
  onClaim: (missionId: string) => void;
  onStart: (mission: Mission) => void;
  clickedMissions: Record<string, boolean>;
  onMissionClicked: (missionId: string) => void;
}) {
  const [linkLoading, setLinkLoading] = useState<Record<string, boolean>>({});
  const linkTimeoutsRef = useRef<Record<string, number | null>>({});

  useEffect(() => {
    return () => {
      if (typeof window === "undefined") return;
      Object.values(linkTimeoutsRef.current).forEach((timeoutId) => {
        if (timeoutId) window.clearTimeout(timeoutId);
      });
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (missions.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-white/60 text-sm">{t("streakMissions.noMissions")}</span>
      </div>
    );
  }

  return (
    <>
      {missions.map((mission) => {
        const isClaimed = mission.isClaimed;
        const isEligible = mission.isEligible;
        const isMutating = activeMissionId === mission.id;
        const url = mission.details.url?.trim();
        const hasUrl = Boolean(url);
        const isUrlFirst = shouldOpenUrlFirst(mission.type);
        const hasClicked = clickedMissions[mission.id];
        const isChecking = Boolean(linkLoading[mission.id]);
        const hasClickedLink = hasUrl && isUrlFirst && hasClicked;
        const shouldShowClaim = isUrlFirst ? hasClickedLink : isEligible;

        const handleStartClick = () => {
          if (isChecking) return;

          if (hasUrl && url && isUrlFirst) {
            if (url.startsWith("/")) {
              onMissionClicked(mission.id);
              onStart(mission);
            } else {
              setLinkLoading((prev) => ({ ...prev, [mission.id]: true }));
              if (typeof window !== "undefined") {
                window.open(url, "_blank", "noopener,noreferrer");
                const timeoutId = window.setTimeout(() => {
                  onMissionClicked(mission.id);
                  setLinkLoading((prev) => {
                    const next = { ...prev };
                    delete next[mission.id];
                    return next;
                  });
                  linkTimeoutsRef.current[mission.id] = null;
                }, 5000);
                linkTimeoutsRef.current[mission.id] = timeoutId;
              } else {
                onMissionClicked(mission.id);
                setLinkLoading((prev) => {
                  const next = { ...prev };
                  delete next[mission.id];
                  return next;
                });
              }
            }
            return;
          }

          onStart(mission);
        };

        return (
          <div
            key={mission.id}
            className="relative flex flex-col rounded-lg items-center p-3 blue-container"
          >
            <div className="flex justify-between w-full">
              <div className="flex flex-col gap-1 flex-1 min-w-0 mr-2">
                <span className="text-white text-sm font-bold">
                  {translateStreakMissionDescription(mission.description)}
                </span>
                {mission.details.threshold && (
                  <span className="text-xs text-zinc-400">
                    ({mission.progress}/{mission.details.threshold})
                  </span>
                )}
                <div className="flex items-center space-x-1">
                  <img
                    src={getRewardIcon(mission.rewardType)}
                    alt="reward"
                    className={cn(
                      mission.rewardType === "lucky_draw"
                        ? "w-4 h-auto"
                        : "h-4 w-4",
                    )}
                  />
                  <span className="text-white font-semibold text-sm">
                    +{mission.rewardAmount}
                  </span>
                </div>
              </div>

              <div className="shrink-0 flex items-center">
                {isClaimed ? (
                  <DefaultButton variant="green" disabled className="h-8 px-3!">
                    <span className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase">
                      <Check className="w-4 h-4" />
                      {t("streakMissions.claimed")}
                    </span>
                  </DefaultButton>
                ) : isChecking ? (
                  <DefaultButton variant="default" disabled className="h-8 px-3!">
                    <AnimatedWaiting
                      prefix={t("missions.checking")}
                      className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase"
                    />
                  </DefaultButton>
                ) : shouldShowClaim ? (
                  <DefaultButton
                    variant="green"
                    disabled={isMutating}
                    onClick={() => onClaim(mission.id)}
                    className="h-8 px-3!"
                  >
                    {isMutating ? (
                      <Spinner className="w-4 h-4" />
                    ) : (
                      <span className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase">
                        {t("streakMissions.claim")}
                      </span>
                    )}
                  </DefaultButton>
                ) : (
                  <DefaultButton
                    variant="default"
                    onClick={handleStartClick}
                    className="h-8 px-3!"
                  >
                    <span className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase">
                      {t("streakMissions.start")}
                    </span>
                  </DefaultButton>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
